import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import {
  Book,
  BookStatus,
  BookSource,
  BookFormat,
  STATUS_META,
  STATUS_ORDER,
  SOURCE_META,
  SOURCE_ORDER,
  FORMAT_META,
  FORMAT_ORDER,
  blankBook,
  loadBooks,
  normalizeBook,
  parseGoodreadsCsv,
  fetchCover,
  fetchMeta,
  searchBooks,
  isKnownGenre,
  KNOWN_GENRES,
  BookSuggestion,
} from "../COMPONENTS/books/books";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import "../CSS/books.scss";

const GENRE_OPTIONS = [...KNOWN_GENRES].sort((a, b) => a.localeCompare(b, "sv"));

type Sort = "added" | "title" | "author" | "rating" | "year";

// Stable pseudo-random spine look (colour, width, height) from the title.
const hashStr = (s: string): number => {
  let h = 0;
  for (let k = 0; k < s.length; k++) h = (h * 31 + s.charCodeAt(k)) >>> 0;
  return h;
};
const spineStyle = (b: Book): React.CSSProperties => {
  const h = hashStr(b.title || b.id);
  const hue = h % 360;
  const width = 30 + (h % 5) * 3; // 30–42px
  const height = 196; // uniform so wrapped shelf rows line up on the boards
  return {
    width,
    height,
    background: `linear-gradient(90deg, hsl(${hue} 32% 15%), hsl(${hue} 36% 28%) 14%, hsl(${hue} 32% 24%) 85%, hsl(${hue} 30% 13%))`,
  };
};

const Stars = ({ value, onChange }: { value: number; onChange?: (n: number) => void }) => (
  <span className="books__stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <span
        key={n}
        className={`books__star${n <= value ? " books__star--on" : ""}${onChange ? " books__star--btn" : ""}`}
        onClick={onChange ? () => onChange(n === value ? 0 : n) : undefined}
      >
        ★
      </span>
    ))}
  </span>
);

export const BooksView = ({ uid }: { uid: string }) => {
  usePageBackground();
  const [books, setBooks] = useState<Book[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const localSeed = useRef<Book[]>(loadBooks()); // captured before any wipe, for one-time migration
  const [filter, setFilter] = useState<BookStatus>("read");
  const [source, setSource] = useState<BookSource | "all">("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<Sort>("added");
  const [layout, setLayout] = useState<"shelf" | "list">(
    () => (localStorage.getItem("books-layout") as "shelf" | "list") || "shelf"
  );
  const chooseLayout = (l: "shelf" | "list") => { setLayout(l); localStorage.setItem("books-layout", l); };
  const [editing, setEditing] = useState<Book | null>(null);
  const [importMsg, setImportMsg] = useState("");
  const [showYear, setShowYear] = useState(false); // "i år" popover
  const fileRef = useRef<HTMLInputElement>(null);
  const yearCardRef = useRef<HTMLDivElement>(null);

  const attempted = useRef<Set<string>>(new Set());
  const chain = useRef<Promise<void>>(Promise.resolve());

  // When a book flips open its cover swings right; slide the books after it in
  // the same shelf row aside so the cover doesn't cover them.
  const shiftRow = (e: React.MouseEvent) => {
    const cur = e.currentTarget as HTMLElement;
    const container = cur.parentElement;
    if (!container) return;
    const kids = Array.from(container.children) as HTMLElement[];
    const idx = kids.indexOf(cur);
    for (let j = idx + 1; j < kids.length; j++) kids[j].style.transform = "translateX(96px)";
  };
  const clearShift = (e: React.MouseEvent) => {
    const container = (e.currentTarget as HTMLElement).parentElement;
    if (!container) return;
    (Array.from(container.children) as HTMLElement[]).forEach((k) => { k.style.transform = ""; });
  };

  // Fetch missing covers in the background — throttled and serialized so we don't
  // hammer the (keyless) cover APIs. Triggered on demand by the button.
  const backfillCovers = (list: Book[]) => {
    const todo = list.filter((b) => b.title && !attempted.current.has(b.id));
    if (todo.length === 0) { setImportMsg("Inga böcker att uppdatera."); return; }
    const total = todo.length;
    let done = 0;
    let found = 0;
    setImportMsg(`Hämtar omslag & genre… 0/${total}`);
    chain.current = chain.current.then(async () => {
      for (const bk of todo) {
        attempted.current.add(bk.id);
        const meta = await fetchMeta(bk.title, bk.author, bk.isbn || "");
        if (meta.cover || meta.genre) found++;
        setBooks((cur) =>
          cur.map((x) =>
            x.id === bk.id
              ? {
                  ...x,
                  // replace with the freshly-fetched (per-title/edition) cover so
                  // wrong ones get corrected; keep existing only if none found
                  coverUrl: meta.cover || x.coverUrl,
                  // re-classify from fresh metadata; keep existing if none found
                  genre: meta.genre || (isKnownGenre(x.genre || "") ? x.genre : ""),
                }
              : x
          )
        );
        done++;
        setImportMsg(`Hämtar omslag & genre… ${done}/${total}`);
        await new Promise((r) => setTimeout(r, 450));
      }
      setImportMsg(`Klart ✓ — uppdaterade ${found} av ${total}.`);
      window.setTimeout(() => setImportMsg((m) => (m.startsWith("Klart") ? "" : m)), 6000);
    });
  };

  // Load from the account (Firestore); migrate any locally-stored books the first
  // time this account has none.
  useEffect(() => {
    let cancelled = false;
    db.collection("users")
      .doc(uid)
      .get()
      .then((snap) => {
        if (cancelled) return;
        const data = snap.data();
        let list: Book[] = Array.isArray(data?.books) ? (data!.books as any[]).map(normalizeBook) : [];
        if (list.length === 0 && localSeed.current.length > 0) {
          list = localSeed.current;
          db.collection("users").doc(uid).set({ books: list }, { merge: true }).catch(() => {});
        }
        setBooks(list);
        setHydrated(true);
      })
      .catch(() => setHydrated(true));
    return () => { cancelled = true; };
  }, [uid]);

  // Persist to the account (debounced), once loaded.
  useEffect(() => {
    if (!hydrated) return;
    const t = window.setTimeout(() => {
      db.collection("users").doc(uid).set({ books }, { merge: true }).catch(() => {});
    }, 800);
    return () => window.clearTimeout(t);
  }, [books, hydrated, uid]);

  // close the "i år" popover when tapping/clicking outside it
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (yearCardRef.current && !yearCardRef.current.contains(e.target as Node)) setShowYear(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  const booksThisYear = useMemo(() => {
    const y = String(new Date().getFullYear());
    return books.filter((b) => b.status === "read" && b.year === y);
  }, [books]);

  const stats = useMemo(() => {
    const read = books.filter((b) => b.status === "read");
    const thisYear = String(new Date().getFullYear());
    return {
      total: books.length,
      read: read.length,
      thisYear: read.filter((b) => b.year === thisYear).length,
      reading: books.filter((b) => b.status === "reading").length,
    };
  }, [books]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = books.filter(
      (b) =>
        b.status === filter &&
        (source === "all" || b.source === source) &&
        (!q || b.title.toLowerCase().includes(q) || b.author.toLowerCase().includes(q))
    );
    const idxOf = (id: string) => books.findIndex((b) => b.id === id);
    list = [...list].sort((a, b) => {
      switch (sort) {
        case "title": return a.title.localeCompare(b.title, "sv");
        case "author": return a.author.localeCompare(b.author, "sv");
        case "rating": return b.rating - a.rating;
        case "year": return (b.year || "").localeCompare(a.year || "");
        default: return idxOf(b.id) - idxOf(a.id); // newest added first
      }
    });
    return list;
  }, [books, filter, source, query, sort]);

  // Genre sections that sit side by side on ONE shelf (label above each group,
  // no line break per genre). "Övrigt" for un-tagged, sorted last.
  const shelves = useMemo(() => {
    const m = new Map<string, Book[]>();
    shown.forEach((b) => {
      const g = (b.genre || "").trim() || "Övrigt";
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(b);
    });
    return Array.from(m.entries()).sort((a, b) =>
      a[0] === "Övrigt" ? 1 : b[0] === "Övrigt" ? -1 : a[0].localeCompare(b[0], "sv")
    );
  }, [shown]);


  const saveBook = (bk: Book) => {
    const clean: Book = { ...bk, title: bk.title.trim() || "Namnlös bok", author: bk.author.trim() };
    setBooks((cur) => (cur.some((b) => b.id === clean.id) ? cur.map((b) => (b.id === clean.id ? clean : b)) : [clean, ...cur]));
    setEditing(null);
    // auto-fetch a cover if none was set
    if (!clean.coverUrl && clean.title) {
      fetchCover(clean.title, clean.author, clean.isbn || "").then((url) => {
        if (url) setBooks((cur) => cur.map((b) => (b.id === clean.id && !b.coverUrl ? { ...b, coverUrl: url } : b)));
      });
    }
  };
  const removeBook = (id: string) => {
    setBooks((cur) => cur.filter((b) => b.id !== id));
    setEditing(null);
  };

  const onImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseGoodreadsCsv(String(reader.result || ""));
      if (parsed.length === 0) { setImportMsg("Hittade inga böcker i filen."); return; }
      // de-dupe against existing by title+author (case-insensitive)
      const seen = new Set(books.map((b) => `${b.title}|${b.author}`.toLowerCase()));
      const fresh = parsed.filter((b) => !seen.has(`${b.title}|${b.author}`.toLowerCase()));
      setBooks((cur) => [...fresh, ...cur]);
      setImportMsg(`Importerade ${fresh.length} böcker${parsed.length - fresh.length ? ` (${parsed.length - fresh.length} fanns redan)` : ""}. Klicka "Hämta saknade omslag" för omslagen.`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const fetchMissingCovers = () => {
    attempted.current.clear(); // allow retrying everything this session
    backfillCovers(books);
  };

  return (
    <div className="books">
      <div className="books__header">
        <NavLink to={routeUrls.PROJEKT} className="books__back">← Projekt</NavLink>
        <h1 className="books__title">Böcker</h1>
        <span className="books__sub">Vad jag läst och lyssnat på</span>
      </div>

      <div className="books__stats">
        <div className="books__stat"><span className="books__stat-num">{stats.read}</span><span className="books__stat-label">lästa</span></div>
        <div
          ref={yearCardRef}
          className={`books__stat books__stat--pop${showYear ? " books__stat--open" : ""}`}
          onPointerDown={(e) => { if (e.pointerType !== "mouse" && booksThisYear.length) setShowYear((v) => !v); }}
        >
          <span className="books__stat-num">{stats.thisYear}</span>
          <span className="books__stat-label">i år</span>
          {booksThisYear.length > 0 && (
            <div className="books__stat-popover">
              <div className="books__stat-pop-head">Lästa i år ({booksThisYear.length})</div>
              <ul className="books__stat-pop-list">
                {[...booksThisYear]
                  .sort((a, b) => a.title.localeCompare(b.title, "sv"))
                  .map((b) => (
                    <li key={b.id}>{b.title}{b.author ? ` — ${b.author}` : ""}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>
        <div className="books__stat"><span className="books__stat-num">{stats.reading}</span><span className="books__stat-label">läser nu</span></div>
        <div className="books__stat"><span className="books__stat-num">{stats.total}</span><span className="books__stat-label">totalt</span></div>
      </div>

      <div className="books__controls">
        <button className="books__add" onClick={() => setEditing(blankBook())}>+ Lägg till bok</button>
        <button className="books__import" onClick={() => fileRef.current?.click()}>📚 Importera Goodreads (CSV)</button>
        <button className="books__import" onClick={fetchMissingCovers}>🖼️ Hämta omslag & genre</button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={onImport} />
        {importMsg && <span className="books__import-msg">{importMsg}</span>}
      </div>

      <div className="books__filters">
        <div className="books__tabs">
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              className={`books__tab${filter === s ? " books__tab--on" : ""}`}
              onClick={() => setFilter(s)}
            >
              {STATUS_META[s].icon} {STATUS_META[s].label}
            </button>
          ))}
        </div>
        <div className="books__filter-right">
          <input
            className="books__search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Sök titel/författare…"
          />
          <select className="books__select" value={source} onChange={(e) => setSource(e.target.value as any)}>
            <option value="all">Alla källor</option>
            {SOURCE_ORDER.map((s) => <option key={s} value={s}>{SOURCE_META[s].icon} {SOURCE_META[s].label}</option>)}
          </select>
          <select className="books__select" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="added">Senast tillagd</option>
            <option value="title">Titel</option>
            <option value="author">Författare</option>
            <option value="rating">Betyg</option>
            <option value="year">År</option>
          </select>
          <div className="books__layout">
            <button className={`books__layout-btn${layout === "shelf" ? " books__layout-btn--on" : ""}`} title="Bokhylla" onClick={() => chooseLayout("shelf")}>▦</button>
            <button className={`books__layout-btn${layout === "list" ? " books__layout-btn--on" : ""}`} title="Lista" onClick={() => chooseLayout("list")}>☰</button>
          </div>
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="books__empty">
          {books.length === 0
            ? "Inga böcker ännu — lägg till en eller importera från Goodreads."
            : `Inga böcker under "${STATUS_META[filter].label}".`}
        </p>
      ) : layout === "shelf" ? (
        <div className="books__shelves">
          <div className="books__shelf">
            {shelves.map(([genre, list]) => (
              <div key={genre} className="books__genre-section">
                <h3 className="books__genre-title">{genre} <span className="books__genre-count">{list.length}</span></h3>
                <div className="books__genre-books">
                  {list.map((b) => {
                    const { background, ...box } = spineStyle(b);
                    return (
                      <div
                        key={b.id}
                        className="books__book3d"
                        style={box}
                        onClick={() => setEditing(b)}
                        onMouseEnter={shiftRow}
                        onMouseLeave={clearShift}
                        title={`${b.title}${b.author ? ` — ${b.author}` : ""}`}
                      >
                        <div className="books__b3-spine" style={{ background }}>
                          <span className="books__spine-status" style={{ background: STATUS_META[b.status].color }} />
                          <span className="books__spine-text">{b.title}</span>
                        </div>
                        <div className="books__b3-cover">
                          {b.coverUrl ? (
                            <img src={b.coverUrl} alt="" loading="lazy" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                          ) : (
                            <span className="books__b3-fallback">{b.title}</span>
                          )}
                          {b.rating > 0 && <span className="books__b3-rating"><Stars value={b.rating} /></span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <ul className="books__list">
          {shown.map((b) => (
            <li key={b.id} className="books__row" onClick={() => setEditing(b)}>
              <span className="books__cover" style={{ background: STATUS_META[b.status].color + "22", color: STATUS_META[b.status].color }}>
                {b.coverUrl ? (
                  <img
                    className="books__cover-img"
                    src={b.coverUrl}
                    alt=""
                    loading="lazy"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                  />
                ) : (
                  SOURCE_META[b.source].icon
                )}
              </span>
              <div className="books__row-main">
                <span className="books__row-title">{b.title}</span>
                {b.author && <span className="books__row-author">{b.author}</span>}
                <div className="books__row-badges">
                  <span className="books__badge" style={{ color: STATUS_META[b.status].color }}>
                    {STATUS_META[b.status].icon} {STATUS_META[b.status].label}
                  </span>
                  {b.format && <span className="books__badge">{FORMAT_META[b.format as Exclude<BookFormat, "">].icon} {FORMAT_META[b.format as Exclude<BookFormat, "">].label}</span>}
                  {b.year && <span className="books__badge">{b.year}</span>}
                </div>
              </div>
              {b.rating > 0 && <Stars value={b.rating} />}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <BookEditor
          initial={editing}
          exists={books.some((b) => b.id === editing.id)}
          onSave={saveBook}
          onCancel={() => setEditing(null)}
          onDelete={() => removeBook(editing.id)}
        />
      )}
    </div>
  );
};

const BookEditor = ({
  initial,
  exists,
  onSave,
  onCancel,
  onDelete,
}: {
  initial: Book;
  exists: boolean;
  onSave: (b: Book) => void;
  onCancel: () => void;
  onDelete: () => void;
}) => {
  const [b, setB] = useState<Book>(initial);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverMsg, setCoverMsg] = useState("");
  const [sug, setSug] = useState<BookSuggestion[]>([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const abort = useRef<AbortController | undefined>(undefined);
  const set = (patch: Partial<Book>) => setB((cur) => ({ ...cur, ...patch }));

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
    abort.current?.abort();
  }, []);

  const onTitle = (v: string) => {
    set({ title: v });
    setShowSug(true);
    if (timer.current) window.clearTimeout(timer.current);
    if (v.trim().length < 3) { setSug([]); setSearching(false); return; }
    setSearching(true);
    timer.current = window.setTimeout(async () => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      const res = await searchBooks(v, ctrl.signal);
      setSug(res);
      setSearching(false);
    }, 350);
  };

  const pickSug = (s: BookSuggestion) => {
    set({
      title: s.title,
      author: s.author || b.author,
      coverUrl: s.coverUrl || b.coverUrl,
      isbn: s.isbn || "",
      genre: s.genre || b.genre,
    });
    setSug([]);
    setShowSug(false);
    // upgrade cover + genre from the exact edition (Google Books by ISBN)
    fetchMeta(s.title, s.author, s.isbn).then((m) => {
      if (m.cover) set({ coverUrl: m.cover });
      setB((cur) => (cur.genre ? cur : { ...cur, genre: m.genre }));
    });
  };

  const getCover = async () => {
    setCoverBusy(true);
    setCoverMsg("");
    const url = await fetchCover(b.title, b.author, b.isbn || "");
    setCoverBusy(false);
    if (url) set({ coverUrl: url });
    else setCoverMsg("Hittade inget omslag.");
  };

  return (
    <div className="books__overlay" onClick={onCancel}>
      <div className="books__modal" onClick={(e) => e.stopPropagation()}>
        <div className="books__modal-head">
          <h3 className="books__modal-title">{exists ? "Redigera bok" : "Ny bok"}</h3>
          <button className="books__modal-close" onClick={onCancel} aria-label="Stäng">×</button>
        </div>
        <div className="books__modal-body">
          <div className="books__cover-edit">
            <div className="books__cover-preview">
              {b.coverUrl ? (
                <img src={b.coverUrl} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }} />
              ) : (
                <span>📕</span>
              )}
            </div>
            <div className="books__cover-actions">
              <button className="books__btn books__btn--ghost" onClick={getCover} disabled={coverBusy || !b.title.trim()}>
                {coverBusy ? "Söker…" : "🔍 Hämta omslag"}
              </button>
              {b.coverUrl && <button className="books__link-btn" onClick={() => set({ coverUrl: "" })}>Rensa</button>}
              {coverMsg && <span className="books__cover-msg">{coverMsg}</span>}
            </div>
          </div>
          <div className="books__autocomplete">
            <input
              className="books__input"
              value={b.title}
              onChange={(e) => onTitle(e.target.value)}
              onFocus={() => sug.length > 0 && setShowSug(true)}
              onBlur={() => window.setTimeout(() => setShowSug(false), 150)}
              placeholder="Titel (sök för att autofylla)"
              autoFocus
              autoComplete="off"
            />
            {showSug && (searching || sug.length > 0) && (
              <ul className="books__sug">
                {searching && sug.length === 0 && (
                  <li className="books__sug-item books__sug-item--muted">Söker…</li>
                )}
                {sug.map((s, i) => (
                  <li
                    key={i}
                    className="books__sug-item"
                    onMouseDown={(e) => { e.preventDefault(); pickSug(s); }}
                  >
                    {s.coverUrl ? (
                      <img className="books__sug-cover" src={s.coverUrl} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }} />
                    ) : (
                      <span className="books__sug-cover">📕</span>
                    )}
                    <span className="books__sug-text">
                      <span className="books__sug-title">{s.title}</span>
                      <span className="books__sug-author">{s.author}{s.year ? ` · ${s.year}` : ""}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input className="books__input" value={b.author} onChange={(e) => set({ author: e.target.value })} placeholder="Författare" />

          <span className="books__form-label">Status</span>
          <div className="books__chips">
            {STATUS_ORDER.map((s) => (
              <button key={s} className={`books__chip${b.status === s ? " books__chip--on" : ""}`} onClick={() => set({ status: s })}>
                {STATUS_META[s].icon} {STATUS_META[s].label}
              </button>
            ))}
          </div>

          <span className="books__form-label">Betyg</span>
          <Stars value={b.rating} onChange={(n) => set({ rating: n })} />

          <div className="books__form-row">
            <label className="books__field">
              <span>Källa</span>
              <select className="books__select" value={b.source} onChange={(e) => set({ source: e.target.value as BookSource })}>
                {SOURCE_ORDER.map((s) => <option key={s} value={s}>{SOURCE_META[s].icon} {SOURCE_META[s].label}</option>)}
              </select>
            </label>
            <label className="books__field">
              <span>Format</span>
              <select className="books__select" value={b.format} onChange={(e) => set({ format: e.target.value as BookFormat })}>
                <option value="">—</option>
                {FORMAT_ORDER.filter(Boolean).map((f) => <option key={f} value={f}>{FORMAT_META[f as Exclude<BookFormat, "">].icon} {FORMAT_META[f as Exclude<BookFormat, "">].label}</option>)}
              </select>
            </label>
            <label className="books__field">
              <span>År läst</span>
              <input className="books__input books__input--num" value={b.year} onChange={(e) => set({ year: e.target.value })} placeholder="2024" />
            </label>
          </div>

          <label className="books__field">
            <span>Genre (hylla)</span>
            <select className="books__select" style={{ width: "100%" }} value={b.genre || ""} onChange={(e) => set({ genre: e.target.value })}>
              <option value="">Övrigt</option>
              {b.genre && !GENRE_OPTIONS.includes(b.genre) && <option value={b.genre}>{b.genre}</option>}
              {GENRE_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>

          <textarea className="books__textarea" value={b.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Anteckningar (valfritt)" />
        </div>
        <div className="books__modal-foot">
          {exists && <button className="books__link-btn books__link-btn--danger" onClick={onDelete}>Ta bort</button>}
          <span className="books__foot-spacer" />
          <button className="books__btn books__btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="books__btn" onClick={() => onSave(b)}>Spara</button>
        </div>
      </div>
    </div>
  );
};
